<template>
	<Modal
		:visible="visible"
		:title="titleText"
		:clickClose="onClose"
		:closeOnOverlayClick="false"
		variant="tabletFullWidth"
	>
		<div class="vw-flat-rate" slot="body" data-testid="vw-flat-rate-modal">
			<div class="vw-flat-rate__search-container">
				<FlatRateSearch
					class="vw-flat-rate__search"
					:vin="vin"
					:modelYear="modelYear"
					:modelName="modelName"
					:manufacturerId="manufacturerId"
					@laborSearch="onLaborSearch"
					:resultCount="laborRatesCount"
				></FlatRateSearch>
				<FlatRateSearchResults
					class="vw-flat-rate__results"
					:recordset="laborRatesItems"
					:loading="appPageLoading"
					:hasSearchFilter="hasSearchFilter"
					:hasMoreResults="hasMoreResults"
					@addLaborCode="onAddLaborCode"
					@addDefectCode="onAddDefectCode"
					@sort="onSort"
					@nextPage="onNextPage"
				></FlatRateSearchResults>
			</div>
			<FlatRateImport
				class="vw-flat-rate__import"
				:defectCode="selectedDefectCode"
				:laborCodes="selectedLaborCodes"
				@removeLaborCode="onRemoveLaborCode"
				@removeDefectCode="onRemoveDefectCode"
				@import="onImport"
				@cancel="onCancelImport"
			></FlatRateImport>
		</div>
	</Modal>
</template>

<script>
import Modal from '@/components/shells/modal/modal.vue';
import FlatRateSearch from '@/sections/service/labor-time-guide/vw-flat-rate/vw-flat-rate-search-view.vue';
import FlatRateSearchResults from '@/sections/service/labor-time-guide/vw-flat-rate/vw-flat-rate-search-results-view.vue';
import FlatRateImport from '@/sections/service/labor-time-guide/vw-flat-rate/vw-flat-rate-import-view.vue';
import { mapActions, mapGetters } from 'vuex';
import Manufacturers from '@/constants/manufacturer-types';
import PageLoaderMixin from '@/mixins/page-loader-mixin';
import headerToastMixin from '@/mixins/header-toast-mixin';

export default {
	name: 'vwFlatRateModal',
	mixins: [PageLoaderMixin, headerToastMixin],
	components: {
		Modal,
		FlatRateSearch,
		FlatRateImport,
		FlatRateSearchResults
	},
	data() {
		return {
			searchParamsData: {},
			defaultFilters: [
				'maxCount',
				'page',
				'sortDirection',
				'companyId',
				'year',
				'model',
				'manufacturerId',
				'sortBy'
			],
			laborSearchData: '',
			scrolling: false,
			sortBy: 'laborOpNumber asc',
			maxCount: 100,
			errorToast: {
				message: '',
				state: 'negative',
				iconClassName: 'icon-warning',
				dismissibleTime: 5,
				siteLevel: false
			}
		};
	},
	props: {
		vin: {
			type: String,
			default: null
		},
		modelYear: {
			type: Number,
			default: null
		},
		modelName: {
			type: String,
			default: null
		},
		manufacturerId: {
			type: Number,
			default: null
		},
		visible: {
			type: Boolean,
			default: false
		},
		companyId: {
			type: Number,
			default: null
		}
	},
	created() {
		this.onReady();
	},
	computed: {
		...mapGetters('app', ['appPageLoading']),
		...mapGetters('vwFlatRate', [
			'laborRatesItems',
			'selectedDefectCode',
			'selectedLaborCodes',
			'laborRatesTotalCount',
			'selectedOpCodes'
		]),
		titleText() {
			if (this.manufacturerId === Manufacturers.Volkswagen)
				return 'VW Labor Time Guide';
			else if (this.manufacturerId === Manufacturers.Audi)
				return 'Audi Labor Time Guide';
			else return 'Labor Time Guide';
		},
		laborRatesCount() {
			return this.laborRatesTotalCount;
		},
		hasMoreResults() {
			let result = false;
			result = this.laborRatesItems.length < this.laborRatesTotalCount;

			return result;
		},
		queryParams() {
			return {
				searchText: this.laborSearchData || undefined,
				manufacturerId: this.manufacturerId || undefined,
				model: this.modelName || undefined,
				year: this.modelYear || undefined,
				sortBy: this.sortBy,
				companyId: parseInt(this.companyId),
				maxCount: this.maxCount ? parseInt(this.maxCount) : 100,
				page: !this.scrolling ? 1 : this.currentPage
			};
		},
		hasResults() {
			return this.laborRatesItems.length > 0;
		},
		hasSearchFilter() {
			let result = false;

			//tells whether any filters have been applied
			//so we know which no reports found message to show
			//default params are companyid, page and maxcount
			for (const property in this.queryParams) {
				if (
					this.defaultFilters.indexOf(property) < 0 &&
					this.queryParams[property] !== undefined
				) {
					result = true;
					break;
				}
			}

			return result;
		}
	},
	methods: {
		...mapActions('vwFlatRate', [
			'getLaborRatesAction',
			'addLaborCodeAction',
			'removeLaborCodeAction',
			'setDefectCodeAction',
			'resetLaborRatesAction'
		]),
		onClose() {
			this.$emit('close');
		},
		onReady() {
			this.$emit('ready');
		},
		async onLaborSearch(searchData) {
			if (!this.appPageLoading) {
				this.showPageLoader();
				if (searchData) this.laborSearchData = searchData.searchText;
				//const params = Object.assign({}, this.queryParams);
				if (!this.scrolling) {
					this.resetLaborRatesAction();
					this.currentPage = 1;
				}
				await this.getLaborRatesAction({ parameters: this.queryParams, errorHandler: this.onHandledError });
				this.scrolling = false;
				this.hidePageLoader();
			}
		},
		onAddLaborCode(laborCode) {
			this.addLaborCodeAction(laborCode);
		},
		onAddDefectCode(defectCode) {
			this.setDefectCodeAction(defectCode);
		},
		onRemoveLaborCode(id) {
			this.removeLaborCodeAction(id);
		},
		onRemoveDefectCode() {
			this.setDefectCodeAction(null);
		},
		onImport() {
			this.$emit('import', {
				laborRates: this.selectedOpCodes
			});
		},
		onCancelImport() {
			this.onClose();
		},
		onSort(objSort) {
			if (objSort) {
				this.sortBy = objSort.columnName + ' ' + objSort.sortDirection;
				this.$nextTick(() => {
					this.onLaborSearch();
				});
			}
		},
		onNextPage() {
			this.scrolling = true;
			this.currentPage += 1;
			this.$nextTick(() => {
				this.onLaborSearch();
			});
		},
		onHandledError(error) {
			let errorMessage = error;
			if (error.response?.data?.message) {
				errorMessage = error.response?.data?.message;
			}
			this.errorToast.message = 'An error ocurred while retrieving Labor Code information. ' + errorMessage;
			this.showHeaderToast(this.errorToast);
			this.$emit('error', this.errorToast);
			this.hidePageLoader();
		}
	}
};
</script>

<style lang="scss" scoped>
.vw-flat-rate {
	max-width: 100%;
	display: flex;
	flex-direction: column;
	gap: $gutter-xl;

	&__search-container {
		width: 100%;
	}

	&__search {
		width: 100%;
		margin-bottom: $gutter-lg;
	}

	&__import {
		width: 100%;

		@include mq($from: lg) {
			width: 450px;
			height: 100%;
		}
	}

	@include mq($from: lg) {
		width: 1168px;
		height: 499px;
		display: flex;
		flex-direction: row;
		align-items: flex-start;
		justify-content: space-between;
		gap: $gutter-xxl;
	}
}
</style>
